import React, { useState, useEffect } from 'react'

import useDownloadPDF from 'hooks/useDownloadPdf'
import { Control, Controller, FieldValues } from 'react-hook-form'

import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import Markdown from 'react-markdown'
import { FORM } from 'appConfig/enums'
import hookForms from 'utils/hookForms'
import { UserTask, UserTaskHowToStep } from '__generated__/api-types-and-hooks'
import AppTutorialError from 'appAssets/AppTutorialError.png'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppVideoPlayer } from 'components/Common/AppVideoPlayer/AppVideoPlayer.component'
import { AppGenerateTutorialLoader } from 'components/Common/AppSvgIcons/AppGenerateTutorialLoader'
import 'components/AppOnboarding/AppChatbot/AppChatBotFlows/styles.css'

interface IAppGenerateTutorial {
  task: UserTask
  planName: string
  isLoading: boolean
  isViewMode: boolean
  generatedHowToDo: string
  tutorialNotFound: boolean
  control: Control<FieldValues>
  structuredSteps?: UserTaskHowToStep[]
}

export const AppGenerateTutorial: React.FC<IAppGenerateTutorial> = ({
  task,
  control,
  planName,
  isLoading,
  isViewMode,
  generatedHowToDo,
  tutorialNotFound,
  structuredSteps,
}) => {
  const [activeTab, setActiveTab] = useState<'text' | 'video'>('text')
  const downloadPdf = useDownloadPDF()
  const data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()

  // Reset tab to text when new tutorial is generated
  useEffect(() => {
    if (!isLoading && (generatedHowToDo || task?.howToDo)) {
      setActiveTab('text')
    }
  }, [isLoading, generatedHowToDo, task?.howToDo])

  const cleanText = (text) => {
    return text.replace(/\*\*/g, '').trim()
  }

  const renderVideoStep = (step: UserTaskHowToStep) => {
    const videos = step.videos || []

    return (
      <div key={step.stepNumber} className="mb-8">
        {/* Step header */}
        <div className="mb-4">
          <h3 className="text-lg font-inter font-semibold text-app-black-100 mb-2">
            {step.stepNumber}.{' '}
            <span className="[&_a]:text-app-blue-600 [&_a]:underline hover:[&_a]:text-app-blue-800">
              <Markdown
                remarkPlugins={[remarkGfm, remarkMath]}
                components={{
                  a: ({ node, ...props }) => (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                  p: ({ node, ...props }) => <span {...props} />,
                }}
              >
                {step.description}
              </Markdown>
            </span>
          </h3>
        </div>

        {/* Videos for this step */}
        {videos.length > 0 && (
          <div className="space-y-4 ml-6">
            {videos.map((video, index) => {
              if (!video) return null
              return (
                <div key={index} className="w-full">
                  <AppVideoPlayer url={video.url} />
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const renderLegacyVideoSteps = (steps) => {
    return steps.map((step, index) => {
      const videos = step.videos || []

      return (
        <div key={index} className="mb-8">
          {/* Step header */}
          <div className="mb-4">
            <h3 className="text-lg font-inter font-semibold text-app-black-100 mb-2">
              {index + 1}.{' '}
              <span className="[&_a]:text-app-blue-600 [&_a]:underline hover:[&_a]:text-app-blue-800">
                <Markdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  components={{
                    a: ({ node, ...props }) => (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                    p: ({ node, ...props }) => <span {...props} />,
                  }}
                >
                  {step.description}
                </Markdown>
              </span>
            </h3>
          </div>

          {/* Videos for this step */}
          {videos.length > 0 && (
            <div className="space-y-4 ml-6">
              {videos.map((video, videoIndex) => {
                if (!video) return null
                return (
                  <div key={videoIndex} className="w-full">
                    <AppVideoPlayer url={video.url} />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )
    })
  }

  if (isLoading) {
    return <AppGenerateTutorialLoader />
  }

  if (tutorialNotFound) {
    return <img className="mt-4 rounded-2xl" src={AppTutorialError} alt="Not Found" />
  }

  if (!isViewMode || (!task?.howToDo && !generatedHowToDo)) {
    return null
  }

  return (
    <div className="mt-6">
      {/* Header with tabs and actions */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm font-inter font-normal text-app-grey-50">Tutorial</span>
          <span className="text-app-grey-50">/</span>

          {/* Tab buttons */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab('text')}
              className={`text-sm font-inter font-medium px-3 py-1 rounded-md transition-colors ${
                activeTab === 'text'
                  ? 'text-app-black-100 bg-app-grey-10'
                  : 'text-app-grey-60 hover:text-app-black-100'
              }`}
            >
              Text
            </button>
            {/* <button
              onClick={() => setActiveTab('video')}
              className={`text-sm font-inter font-medium px-3 py-1 rounded-md transition-colors ${
                activeTab === 'video'
                  ? 'text-app-black-100 bg-app-grey-10'
                  : 'text-app-grey-60 hover:text-app-black-100'
              }`}
            >
              Video Tutorial
            </button> */}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <AppButton
            label={activeTab === 'text' ? 'Video Tutorial' : 'Read Tutorial'}
            variant="secondary"
            size="sm"
            onClick={() => {
              if (activeTab === 'text') {
                setActiveTab('video')
              } else {
                setActiveTab('text')
              }
            }}
          />
          <AppButton
            label="Download PDF"
            variant="primary"
            size="sm"
            onClick={() => {
              downloadPdf(
                null,
                `How-to-do-${(task?.name || '').replace(/ /g, '-')}.pdf`,
                `${data.name} Tutorial`,
                data.instructionForTask,
                cleanText(generatedHowToDo || task?.howToDo || '')
              )
            }}
          />
        </div>
      </div>

      {/* Content area */}
      <div id="tutorial-content" className="min-h-[200px]">
        {activeTab === 'text' ? (
          // Text Tutorial Tab
          <div className="prose prose-sm max-w-none">
            <div className="text-sm text-app-black-100 font-inter [&_a]:text-app-blue-600 [&_a]:underline hover:[&_a]:text-app-blue-800 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-3 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mb-4 [&_h2]:text-base [&_h2]:font-medium [&_h2]:mb-3 [&_p]:mb-4 [&_p]:leading-relaxed">
              <Markdown
                remarkPlugins={[remarkGfm, remarkMath]}
                components={{
                  a: ({ node, ...props }) => (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol {...props} className="list-decimal pl-6 space-y-3" />
                  ),
                  li: ({ node, ...props }) => <li {...props} className="mb-3 leading-relaxed" />,
                }}
              >
                {generatedHowToDo || task?.howToDo || ''}
              </Markdown>
            </div>
          </div>
        ) : (
          // Video Tutorial Tab
          <div className="space-y-6">
            {(() => {
              // Check if we have structured steps
              if (structuredSteps && structuredSteps.length > 0) {
                // Check if any step has videos
                const hasAnyVideos = structuredSteps.some(
                  (step) =>
                    step.videos &&
                    step.videos.length > 0 &&
                    step.videos.some((video) => video !== null)
                )

                if (!hasAnyVideos) {
                  return (
                    <div className="text-center py-16 bg-app-grey-5 rounded-lg border border-app-grey-20">
                      <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 mx-auto mb-4 bg-app-grey-20 rounded-full flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-app-grey-50"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-inter font-medium text-app-black-100 mb-2">
                          No Video Tutorials Available
                        </h3>
                        <p className="text-sm text-app-grey-60 font-inter mb-4">
                          We couldn't find relevant video tutorials for this task. The text tutorial
                          provides complete step-by-step instructions.
                        </p>
                        <AppButton
                          label="View Text Tutorial"
                          variant="primary"
                          size="sm"
                          onClick={() => setActiveTab('text')}
                        />
                      </div>
                    </div>
                  )
                }

                return <div>{structuredSteps.map((step) => renderVideoStep(step))}</div>
              }

              // Legacy format fallback
              return (
                <Controller
                  control={control}
                  name="howToLinks"
                  render={({ field }) => {
                    if (!field?.value || field?.value.length === 0 || !field?.value[0].steps) {
                      return (
                        <div className="text-center py-16 bg-app-grey-5 rounded-lg border border-app-grey-20">
                          <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 mx-auto mb-4 bg-app-grey-20 rounded-full flex items-center justify-center">
                              <svg
                                className="w-8 h-8 text-app-grey-50"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                            <h3 className="text-lg font-inter font-medium text-app-black-100 mb-2">
                              No Video Tutorials Available
                            </h3>
                            <p className="text-sm text-app-grey-60 font-inter mb-4">
                              We couldn't find relevant video tutorials for this task. The text
                              tutorial provides complete step-by-step instructions.
                            </p>
                            <AppButton
                              label="View Text Tutorial"
                              variant="primary"
                              size="sm"
                              onClick={() => setActiveTab('text')}
                            />
                          </div>
                        </div>
                      )
                    }

                    const steps = JSON.parse(field?.value[0].steps)

                    // Check if any step has videos in legacy format
                    const hasAnyVideos = steps.some((step) => step.videos && step.videos.length > 0)

                    if (!hasAnyVideos) {
                      return (
                        <div className="text-center py-16 bg-app-grey-5 rounded-lg border border-app-grey-20">
                          <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 mx-auto mb-4 bg-app-grey-20 rounded-full flex items-center justify-center">
                              <svg
                                className="w-8 h-8 text-app-grey-50"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                            <h3 className="text-lg font-inter font-medium text-app-black-100 mb-2">
                              No Video Tutorials Available
                            </h3>
                            <p className="text-sm text-app-grey-60 font-inter mb-4">
                              We couldn't find relevant video tutorials for this task. The text
                              tutorial provides complete step-by-step instructions.
                            </p>
                            <AppButton
                              label="View Text Tutorial"
                              variant="primary"
                              size="sm"
                              onClick={() => setActiveTab('text')}
                            />
                          </div>
                        </div>
                      )
                    }

                    return <div>{renderLegacyVideoSteps(steps)}</div>
                  }}
                />
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
